import { useLocalSearchParams, useRouter } from "expo-router";
import { useMemo } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { fontFamily, space, textStyle } from "@passfirst/ui";

import { ScreenAppbar } from "../src/components/screen-appbar";
import { PlanCard } from "../src/paywall/plan-card";
import { fmtUsd, usePaywall } from "../src/paywall/use-paywall";
import { type Colors, useColors } from "../src/theme";
import { useResponsive } from "../src/use-responsive";
import { useSelectedVertical } from "../src/use-selected-vertical";

// Standalone paywall — split out from the practice EXHAUSTED branch
// per the 2026 design refresh. Reached from /diagnostic via the
// "See your options →" CTA. Vertical + jurisdiction context arrive
// via search params; usePaywall fetches the live pricing.
export default function Paywall(): JSX.Element {
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isPaywallWide } = useResponsive();
  const vertical = useSelectedVertical();
  const params = useLocalSearchParams<{
    vertical?: string;
    jurisdiction?: string;
  }>();
  const verticalSlug =
    typeof params.vertical === "string"
      ? params.vertical
      : (vertical.verticalSlug ?? null);
  const jurisdictionCode =
    typeof params.jurisdiction === "string"
      ? params.jurisdiction
      : (vertical.jurisdictionCode ?? null);

  const paywall = usePaywall(verticalSlug, jurisdictionCode);

  if (paywall.error && !paywall.info) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{paywall.error}</Text>
        <Pressable onPress={() => router.back()} style={styles.ghost}>
          <Text style={styles.ghostText}>Back</Text>
        </Pressable>
      </View>
    );
  }
  if (!paywall.info) {
    return (
      <View style={styles.errorContainer}>
        <ActivityIndicator color={colors.accentSoftText} />
      </View>
    );
  }

  const info = paywall.info;
  // Item 8: Prefer live store pricing on native; fall back to server USD prices on web.
  const oneOff =
    info.storePricing?.oneoff ?? fmtUsd(info.pricing.oneOffCents);
  const monthly =
    info.storePricing?.monthly ??
    (info.pricing.subscriptionMonthlyCents
      ? fmtUsd(info.pricing.subscriptionMonthlyCents)
      : null);
  const annual =
    info.storePricing?.annual ??
    (info.pricing.subscriptionAnnualCents
      ? fmtUsd(info.pricing.subscriptionAnnualCents)
      : null);
  const annualSavingsPct =
    info.pricing.subscriptionMonthlyCents &&
    info.pricing.subscriptionAnnualCents
      ? Math.round(
          (1 -
            info.pricing.subscriptionAnnualCents /
              (info.pricing.subscriptionMonthlyCents * 12)) *
            100,
        )
      : null;

  const accessLabel = info.jurisdictionName
    ? `${info.jurisdictionName} ${info.shortName}`
    : info.shortName;

  return (
    <View style={styles.outer}>
      <ScreenAppbar onBack={() => router.back()} />

      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          isPaywallWide && styles.scrollContentWide,
        ]}
      >
        <Text style={styles.eyebrow}>
          UNLOCK THE FULL {info.shortName.toUpperCase()} PREP
        </Text>
        <Text style={styles.title}>
          Keep going until{"\n"}you&apos;re ready.
        </Text>
        <Text style={styles.lede}>
          The entire adaptive pool, unlimited tutor, and the voice-mode coach.
          Pick whichever fits — no wrong choice.
        </Text>

        {/* Rule A sibling (design_handoff_2): cards stack <760, row ≥760. */}
        <View style={isPaywallWide ? styles.cardRow : undefined}>
          <View style={isPaywallWide ? styles.cardCol : undefined}>
            <PlanCard
              primary
              eyebrow="One-time purchase"
              price={oneOff}
              description={`Full ${accessLabel} access until you pass. No subscription.`}
              features={
                isPaywallWide
                  ? [
                      "The entire adaptive question pool",
                      "Unlimited Socratic tutor",
                      "Voice-mode coach",
                      "Diagnostics & mastery tracking",
                    ]
                  : undefined
              }
              ctaLabel={paywall.busy ? "…" : `Unlock for ${oneOff}`}
              ctaVariant="coral"
              onCtaPress={() => void paywall.checkout("oneoff")}
              disabled={paywall.busy}
              testID="paywall-oneoff"
            />
          </View>

          {monthly ? (
            <View style={isPaywallWide ? styles.cardCol : undefined}>
              <PlanCard
                eyebrow="Or subscribe"
                price={monthly}
                unit="/mo"
                description="Useful across multiple credentials. Cancel any time — one tap."
                features={
                  isPaywallWide
                    ? [
                        "Everything in the one-time plan",
                        "Access to all live credentials",
                        ...(annual && annualSavingsPct && annualSavingsPct > 0
                          ? [`${annual}/yr — save ${annualSavingsPct}%`]
                          : []),
                      ]
                    : undefined
                }
                ctaLabel="Subscribe monthly"
                ctaVariant="soft"
                onCtaPress={() => void paywall.checkout("monthly")}
                disabled={paywall.busy}
                testID="paywall-monthly"
                // When a monthly plan exists, surface annual as a compact
                // tertiary link beneath it (the common both-plans case).
                belowCta={
                  annual ? (
                    <Pressable
                      onPress={() => void paywall.checkout("annual")}
                      disabled={paywall.busy}
                      accessibilityRole="button"
                      accessibilityLabel={
                        annualSavingsPct && annualSavingsPct > 0
                          ? `Pay ${annual} per year, save ${annualSavingsPct} percent`
                          : `Pay ${annual} per year`
                      }
                      style={styles.tertiary}
                      testID="paywall-annual"
                    >
                      <Text style={styles.tertiaryText}>
                        {annualSavingsPct && annualSavingsPct > 0
                          ? `Or pay ${annual}/year — save ${annualSavingsPct}%`
                          : `Or pay ${annual}/year`}
                      </Text>
                    </Pressable>
                  ) : null
                }
              />
            </View>
          ) : null}

          {/* Annual-only verticals have no monthly card, so the annual
              option above never renders. Show a standalone annual card
              whenever there's an annual price but no monthly price. */}
          {annual && !monthly ? (
            <View style={isPaywallWide ? styles.cardCol : undefined}>
              <PlanCard
                eyebrow="Or subscribe"
                price={annual}
                unit="/yr"
                description="Useful across multiple credentials. Cancel any time — one tap."
                features={
                  isPaywallWide
                    ? [
                        "Everything in the one-time plan",
                        "Access to all live credentials",
                      ]
                    : undefined
                }
                ctaLabel="Subscribe annually"
                ctaVariant="soft"
                onCtaPress={() => void paywall.checkout("annual")}
                disabled={paywall.busy}
                testID="paywall-annual"
              />
            </View>
          ) : null}
        </View>

        <Text style={styles.promo}>
          Have a promo code? Add it at checkout.
        </Text>
        {isPaywallWide ? (
          <Text style={styles.refund}>14-day refund, no questions.</Text>
        ) : null}

        {paywall.errorKind === "conflict" && paywall.error ? (
          <View style={styles.conflictCallout}>
            <Text style={styles.conflictText}>{paywall.error}</Text>
          </View>
        ) : paywall.error ? (
          <Text style={styles.errorText}>{paywall.error}</Text>
        ) : null}
      </ScrollView>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    outer: { flex: 1, backgroundColor: colors.background },
    errorContainer: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
      gap: space[4],
    },
    scrollContent: {
      padding: space[6],
      paddingBottom: space[10],
      maxWidth: 480,
      alignSelf: "center",
      width: "100%",
    },
    scrollContentWide: { maxWidth: 760 },
    cardRow: {
      flexDirection: "row",
      gap: space[4],
      alignItems: "flex-start",
    },
    cardCol: { flex: 1 },
    eyebrow: {
      fontFamily: fontFamily.monoMedium,
      fontSize: 11,
      letterSpacing: 1.4,
      color: colors.action,
      marginBottom: space[3],
    },
    title: {
      fontFamily: fontFamily.extraBold,
      fontSize: 28,
      lineHeight: 32,
      letterSpacing: -0.5,
      color: colors.heading,
      marginBottom: space[3],
    },
    lede: {
      ...textStyle.body,
      color: colors.textSecondary,
      marginBottom: space[6],
    },
    tertiary: {
      alignItems: "center",
      paddingVertical: space[3],
      marginTop: space[2],
    },
    tertiaryText: {
      ...textStyle.bodySmall,
      color: colors.accentSoftText,
      textDecorationLine: "underline",
    },
    promo: {
      textAlign: "center",
      color: colors.textMuted,
      ...textStyle.caption,
      marginTop: space[3],
    },
    refund: {
      textAlign: "center",
      color: colors.textMuted,
      ...textStyle.caption,
      marginTop: space[2],
    },
    ghost: { paddingVertical: space[3], paddingHorizontal: space[5] },
    ghostText: { ...textStyle.body, color: colors.textSecondary },
    errorText: {
      ...textStyle.bodySmall,
      color: colors.error,
      textAlign: "center",
      marginTop: space[4],
    },
    conflictCallout: {
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.error,
      borderRadius: 8,
      padding: space[4],
      marginTop: space[4],
    },
    conflictText: {
      ...textStyle.bodySmall,
      color: colors.error,
      textAlign: "center",
    },
  });
}
